import type { OrderRecord } from '@starkey/shared'

const STORAGE_KEY = 'starkey-paid-orders'
const ACCESS_STORAGE_KEY = 'starkey-order-access'
const MAX_ITEMS = 8
const MAX_ACCESS_ITEMS = 24

type OrderAccessRecord = {
  orderNo: string
  phone: string
  email: string
  updatedAt: string
}

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

export function isLookupVisibleOrder(order: OrderRecord) {
  return order.paymentStatus === 'SUCCESS' || ['PAID', 'FULFILLING', 'FULFILLED'].includes(order.orderStatus)
}

export function readCachedPaidOrders() {
  if (!canUseStorage()) {
    return [] as OrderRecord[]
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return []
    }

    const parsed = JSON.parse(raw) as OrderRecord[]
    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed.filter(item => item && typeof item.orderNo === 'string')
  } catch {
    return []
  }
}

function readStoredOrderAccess() {
  if (!canUseStorage()) {
    return [] as OrderAccessRecord[]
  }

  try {
    const raw = window.localStorage.getItem(ACCESS_STORAGE_KEY)
    if (!raw) {
      return []
    }

    const parsed = JSON.parse(raw) as OrderAccessRecord[]
    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed.filter(item => item && typeof item.orderNo === 'string' && typeof item.phone === 'string' && typeof item.email === 'string')
  } catch {
    return []
  }
}

function writeStoredOrderAccess(records: OrderAccessRecord[]) {
  if (!canUseStorage()) {
    return
  }

  window.localStorage.setItem(ACCESS_STORAGE_KEY, JSON.stringify(records.slice(0, MAX_ACCESS_ITEMS)))
}

export function writeCachedPaidOrders(orders: OrderRecord[]) {
  if (!canUseStorage()) {
    return
  }

  const merged = [...orders, ...readCachedPaidOrders()].filter(isLookupVisibleOrder)
  const deduped = merged.filter((order, index, array) => array.findIndex(item => item.orderNo === order.orderNo) === index)

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(deduped.slice(0, MAX_ITEMS)))
}

export function cachePaidOrders(orders: OrderRecord[]) {
  const paidOrders = orders.filter(isLookupVisibleOrder)
  if (paidOrders.length === 0) {
    return
  }

  writeCachedPaidOrders(paidOrders)
  rememberOrderAccess(paidOrders)
}

export function rememberOrderAccess(orders: Array<Pick<OrderRecord, 'orderNo' | 'phone' | 'email'>>) {
  if (!canUseStorage()) {
    return
  }

  const merged = [
    ...orders.map(order => ({
      orderNo: order.orderNo,
      phone: order.phone,
      email: order.email,
      updatedAt: new Date().toISOString(),
    })),
    ...readStoredOrderAccess(),
  ]

  const deduped = merged.filter((record, index, array) => array.findIndex(item => item.orderNo === record.orderNo) === index)
  writeStoredOrderAccess(deduped)
}

export function readOrderAccess(orderNo: string) {
  return readStoredOrderAccess().find(item => item.orderNo === orderNo)
}

export function removeCachedPaidOrder(orderNo: string) {
  if (!canUseStorage()) {
    return
  }

  const nextOrders = readCachedPaidOrders().filter(order => order.orderNo !== orderNo)
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextOrders))
}

export function removeOrderAccess(orderNo: string) {
  if (!canUseStorage()) {
    return
  }

  const nextRecords = readStoredOrderAccess().filter(record => record.orderNo !== orderNo)
  writeStoredOrderAccess(nextRecords)
}

export function forgetOrderRecord(orderNo: string) {
  removeCachedPaidOrder(orderNo)
  removeOrderAccess(orderNo)
}
